import React, { useState, useEffect } from 'react';
import { 
  ImageIcon, Type, Palette, Shield, Eye, EyeOff,
  Save, RefreshCw, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WatermarkSettings = () => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    text: '',
    position: 'bottom-right',
    opacity: 70,
    fontSize: 'medium',
    color: '#ffffff',
    showOnPreview: true,
    showOnDownload: true
  });
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    fetchWatermarkSettings();
  }, []);

  const fetchWatermarkSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/settings/watermark`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching watermark settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/client/settings/watermark`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Watermark settings saved successfully' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to save watermark settings' });
    } finally {
      setLoading(false);
    }
  };

  const positions = [
    { id: 'top-left', label: 'Top Left' },
    { id: 'top-right', label: 'Top Right' },
    { id: 'center', label: 'Center' },
    { id: 'bottom-left', label: 'Bottom Left' },
    { id: 'bottom-right', label: 'Bottom Right' },
  ];

  const fontSizes = [
    { id: 'small', label: 'Small', preview: 'text-xs' },
    { id: 'medium', label: 'Medium', preview: 'text-sm' },
    { id: 'large', label: 'Large', preview: 'text-base' },
    { id: 'xlarge', label: 'X-Large', preview: 'text-lg' },
  ];

  return (
    <div className="space-y-6">
      {/* Enable/Disable */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                Content Protection
              </CardTitle>
              <CardDescription className="text-gray-400">
                Add watermarks to protect your content from unauthorized use
              </CardDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </CardHeader>
      </Card>

      {settings.enabled && (
        <>
          {/* Watermark Text */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Type className="w-5 h-5 text-yellow-400" />
                Watermark Text
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400 mb-2 block">Custom Text (leave empty to use username)</Label>
                <Input
                  value={settings.text}
                  onChange={(e) => setSettings(prev => ({ ...prev, text: e.target.value }))}
                  placeholder={`@${user?.username || 'username'}`}
                  className="bg-gray-900 border-gray-600 text-white"
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-gray-400 mb-2 block">Text Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.color}
                      onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                      className="w-12 h-10 p-1 bg-gray-900 border-gray-600 cursor-pointer"
                    />
                    <Input
                      value={settings.color}
                      onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                      placeholder="#ffffff"
                      className="bg-gray-900 border-gray-600 text-white flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Position & Style */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-400" />
                Position & Style
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Position Grid */}
              <div>
                <Label className="text-gray-400 mb-3 block">Watermark Position</Label>
                <div className="grid grid-cols-3 gap-2 max-w-xs">
                  {['top-left', 'top-center', 'top-right', 'center-left', 'center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'].map((pos) => (
                    <button
                      key={pos}
                      onClick={() => setSettings(prev => ({ ...prev, position: pos }))}
                      className={`p-3 rounded-lg border transition-all ${
                        settings.position === pos
                          ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                          : 'bg-gray-900/50 border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      <div className="w-2 h-2 rounded-full bg-current mx-auto" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size */}
              <div>
                <Label className="text-gray-400 mb-3 block">Font Size</Label>
                <div className="flex gap-2">
                  {fontSizes.map((size) => (
                    <button
                      key={size.id}
                      onClick={() => setSettings(prev => ({ ...prev, fontSize: size.id }))}
                      className={`px-4 py-2 rounded-lg border transition-all ${
                        settings.fontSize === size.id
                          ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                          : 'bg-gray-900/50 border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Opacity */}
              <div>
                <Label className="text-gray-400 mb-3 block">Opacity: {settings.opacity}%</Label>
                <Slider
                  value={[settings.opacity]}
                  onValueChange={([value]) => setSettings(prev => ({ ...prev, opacity: value }))}
                  min={10}
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Visibility Options */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-400" />
                Visibility Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-white font-medium">Show on Preview</p>
                    <p className="text-sm text-gray-400">Display watermark when content is previewed</p>
                  </div>
                </div>
                <Switch
                  checked={settings.showOnPreview}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showOnPreview: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-white font-medium">Show on Download</p>
                    <p className="text-sm text-gray-400">Embed watermark in downloaded files</p>
                  </div>
                </div>
                <Switch
                  checked={settings.showOnDownload}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showOnDownload: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-pink-400" />
                  Preview
                </span>
                <Button
                  onClick={() => setPreviewVisible(!previewVisible)}
                  size="sm"
                  variant="outline"
                  className="border-gray-600"
                >
                  {previewVisible ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {previewVisible ? 'Hide' : 'Show'} Preview
                </Button>
              </CardTitle>
            </CardHeader>
            {previewVisible && (
              <CardContent>
                <div className="relative w-full h-48 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg overflow-hidden">
                  {/* Placeholder image */}
                  <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                    <ImageIcon className="w-16 h-16" />
                  </div>
                  
                  {/* Watermark */}
                  <div 
                    className={`absolute ${
                      settings.position.includes('top') ? 'top-4' : 
                      settings.position.includes('bottom') ? 'bottom-4' : 
                      'top-1/2 -translate-y-1/2'
                    } ${
                      settings.position.includes('left') ? 'left-4' :
                      settings.position.includes('right') ? 'right-4' :
                      'left-1/2 -translate-x-1/2'
                    }`}
                    style={{ 
                      color: settings.color,
                      opacity: settings.opacity / 100
                    }}
                  >
                    <span className={`font-bold ${
                      settings.fontSize === 'small' ? 'text-xs' :
                      settings.fontSize === 'medium' ? 'text-sm' :
                      settings.fontSize === 'large' ? 'text-base' :
                      'text-lg'
                    }`}>
                      {settings.text || `@${user?.username || 'username'}`}
                    </span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        </>
      )}

      {/* Info Banner */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          <p className="font-medium text-blue-400 mb-1">About Watermarks</p>
          <p>Watermarks help protect your content from unauthorized use. They&apos;re visible on previews and can be embedded into downloaded files. Premium subscribers may have access to unwatermarked versions based on your settings.</p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={loading}
          className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
        >
          {loading ? (
            <span className="flex items-center">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...
            </span>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Watermark Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default WatermarkSettings;
